import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '#environments';
import { map, Observable, of, switchMap } from 'rxjs';
import type { WorkflowItem } from '#models';
import type { PersistedCaseDetail, PersistedCaseHeader } from '../../models/persisted-case.model';
import { toPersistedCaseItem } from '../../features/workflow-inbox/detail/mappers/persisted-case.mapper';

interface AgentCasePage {
  items: PersistedCaseHeader[];
  nextDataForDate: string | null;
  nextAgentCaseId: number | null;
}

@Injectable({ providedIn: 'root' })
export class CaseService {
  private readonly _http = inject(HttpClient);

  search(asset: string | null, facilityId: string | null) {
    if (environment.useMockData) {
      return of<WorkflowItem[]>([]);
    }
    let params = new HttpParams();
    if (asset) params = params.set('asset', asset);
    if (facilityId) params = params.set('facilityId', facilityId);
    const loadPage = (query: HttpParams) => this._http.get<AgentCasePage>(`${environment.apiUrl}/agentcase`, { params: query });
    return loadPage(params.set('pageSize', 100)).pipe(
      switchMap(page => {
        const collect = (result: AgentCasePage, items: PersistedCaseHeader[]): Observable<PersistedCaseHeader[]> => {
          const collected = [...items, ...result.items];
          if (!result.nextDataForDate || result.nextAgentCaseId == null) return of(collected);
          return loadPage(params.set('pageSize', 100)
            .set('afterDataForDate', result.nextDataForDate)
            .set('afterAgentCaseId', result.nextAgentCaseId)).pipe(
              switchMap(next => collect(next, collected)),
            );
        };
        return collect(page, []);
      }),
      map(headers => headers.map(header => toPersistedCaseItem(header))),
    );
  }

  getDetail(id: number) {
    return this._http.get<PersistedCaseDetail>(`${environment.apiUrl}/agentcase/${id}`)
      .pipe(map(detail => toPersistedCaseItem(detail.header, detail)));
  }
}
